/**
 * Notification API Service
 * Handles API calls for notification management
 */

import { apiClient } from '../utils/apiClient';
import { subdomainDetector } from '../utils/subdomainDetector';

// Base URLs - Context-aware API endpoints
const getNotificationBaseUrl = (): string => {
  const context = subdomainDetector.getSubdomainInfo();
  console.log('🔗 Notifications API - getting base URL for context:', context);
  
  if (context.isEnterprise) {
    console.log('🏢 Notifications API - using enterprise organization endpoint');
    return '/v1/platform/organization/notifications';
  } else {
    console.log('🏠 Notifications API - using tenant endpoint');
    return '/v1/core/notifications';
  }
};

// API Configuration - Dynamic based on context
const NOTIFICATION_BASE_URL = getNotificationBaseUrl();

// Types
export interface NotificationChannel {
  channel_id: string;
  name: string;
  channel_type: 'email' | 'sms' | 'whatsapp';
  provider: string;
  is_active: boolean;
  health_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  rate_limit_per_minute: number;
  cost_per_message: number;
  messages_sent_today: number;
  messages_sent_month: number;
  configuration: any;
  last_health_check?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplate {
  template_id: string;
  name: string;
  template_type: string;
  channel_type: 'email' | 'sms' | 'whatsapp';
  context: 'enterprise' | 'tenant' | 'both';
  language: string;
  subject?: string;
  content: string;
  html_content?: string;
  available_variables: string[];
  tags: string[];
  is_active: boolean;
  usage_count: number;
  last_used?: string;
  created_at: string;
  updated_at: string;
}

export interface TenantNotificationSetting {
  settings_id: string;
  tenant_id: string;
  notification_type: string;
  description: string;
  is_enabled: boolean;
  default_channel_type: 'email' | 'sms' | 'whatsapp';
  send_to_patients: boolean;
  send_to_staff: boolean;
  additional_recipients: string[];
  patient_opt_out_allowed: boolean;
  patient_communication_hours?: {
    start_hour: number;
    end_hour: number;
    timezone: string;
  };
  schedule_type: 'immediate' | 'scheduled' | 'recurring';
  schedule_config?: any;
  subject_override?: string;
  content_override?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationLog {
  notification_id: string;
  context: 'enterprise' | 'tenant';
  tenant_id: string;
  notification_type: string;
  channel_type: 'email' | 'sms' | 'whatsapp';
  channel: string;
  recipient_type: string;
  recipient_id: string;
  recipient_address: string;
  subject: string;
  content: string;
  status: 'pending' | 'sent' | 'failed' | 'retrying';
  sent_at?: string;
  provider_message_id?: string;
  provider_response?: any;
  error_message?: string;
  cost?: number;
  created_at: string;
}

export interface SendNotificationRequest {
  notification_type: string;
  context: 'enterprise' | 'tenant';
  tenant_id?: string;
  recipient_type: string;
  recipient_id: string;
  recipient_address: string;
  channel_type: 'email' | 'sms' | 'whatsapp';
  template_variables?: Record<string, any>;
  custom_content?: {
    subject?: string;
    content: string;
    html_content?: string;
  };
}

// API Response Types
interface ApiResponse<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

// Notification Channels API
export const fetchNotificationChannels = async (params?: {
  channel_type?: string;
  is_active?: boolean;
  health_status?: string;
  page?: number;
  page_size?: number;
}): Promise<NotificationChannel[]> => {
  try {
    const response = await apiClient.get<ApiResponse<NotificationChannel>>(`${NOTIFICATION_BASE_URL}/channels/`, {
      params
    });
    return response.data.results;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification channels');
  }
};

export const getNotificationChannel = async (channelId: string): Promise<NotificationChannel> => {
  try {
    const response = await apiClient.get<NotificationChannel>(`${NOTIFICATION_BASE_URL}/channels/${channelId}/`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification channel');
  }
};

export const testChannelHealth = async (channelId: string): Promise<{ success: boolean; health_status: string; details: any }> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/channels/${channelId}/test_health/`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to test channel health');
  }
};

export const sendTestNotification = async (channelId: string, testRecipient: string): Promise<{ success: boolean; notification_id?: string; error?: string }> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/channels/${channelId}/send_test_message/`, {
      test_recipient: testRecipient
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to send test notification');
  }
};

// Notification Templates API
export const fetchNotificationTemplates = async (params?: {
  template_type?: string;
  channel_type?: string;
  context?: string;
  language?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}): Promise<NotificationTemplate[]> => {
  try {
    const response = await apiClient.get<ApiResponse<NotificationTemplate>>(`${NOTIFICATION_BASE_URL}/templates/`, {
      params
    });
    return response.data.results;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification templates');
  }
};

export const getNotificationTemplate = async (templateId: string): Promise<NotificationTemplate> => {
  try {
    const response = await apiClient.get<NotificationTemplate>(`${NOTIFICATION_BASE_URL}/templates/${templateId}/`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification template');
  }
};

export const previewTemplate = async (templateId: string, sampleVariables?: Record<string, any>): Promise<{
  success: boolean;
  rendered: {
    subject: string;
    content: string;
    html_content: string;
  };
  variables_used: Record<string, any>;
}> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/templates/${templateId}/preview/`, {
      sample_variables: sampleVariables || {}
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to preview template');
  }
};

// Tenant Notification Settings API
export const fetchTenantNotificationSettings = async (): Promise<TenantNotificationSetting[]> => {
  try {
    // For now, return mock data since the backend endpoint doesn't exist yet
    // TODO: Implement proper tenant notification settings endpoint in backend
    console.warn('Using mock tenant notification settings - backend endpoint not implemented');
    return [
      {
        settings_id: '1',
        tenant_id: 'tenant-1',
        notification_type: 'appointment_reminder',
        description: 'Send appointment reminders to patients',
        is_enabled: true,
        default_channel_type: 'email',
        send_to_patients: true,
        send_to_staff: false,
        additional_recipients: [],
        patient_opt_out_allowed: true,
        patient_communication_hours: { start_hour: 9, end_hour: 17, timezone: 'UTC' },
        schedule_type: 'scheduled',
        schedule_config: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        settings_id: '2',
        tenant_id: 'tenant-1',
        notification_type: 'result_ready',
        description: 'Notify when lab results are ready',
        is_enabled: true,
        default_channel_type: 'email',
        send_to_patients: true,
        send_to_staff: true,
        additional_recipients: [],
        patient_opt_out_allowed: false,
        patient_communication_hours: { start_hour: 8, end_hour: 18, timezone: 'UTC' },
        schedule_type: 'immediate',
        schedule_config: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch tenant notification settings');
  }
};

export const getTenantNotificationSetting = async (settingsId: string): Promise<TenantNotificationSetting> => {
  try {
    const response = await apiClient.get<TenantNotificationSetting>(`${NOTIFICATION_BASE_URL}/tenant-settings/${settingsId}/`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch tenant notification setting');
  }
};

export const createTenantNotificationSetting = async (data: Partial<TenantNotificationSetting>): Promise<TenantNotificationSetting> => {
  try {
    const response = await apiClient.post<TenantNotificationSetting>(`${NOTIFICATION_BASE_URL}/tenant-settings/`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create tenant notification setting');
  }
};

export const updateTenantNotificationSetting = async (settingsId: string, data: Partial<TenantNotificationSetting>): Promise<TenantNotificationSetting> => {
  try {
    const response = await apiClient.patch<TenantNotificationSetting>(`${NOTIFICATION_BASE_URL}/tenant-settings/${settingsId}/`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update tenant notification setting');
  }
};

export const updateTenantNotificationSettings = async (data: Partial<TenantNotificationSetting>): Promise<TenantNotificationSetting> => {
  // Find existing setting or create new one
  try {
    const existingSettings = await fetchTenantNotificationSettings();
    const existingSetting = existingSettings.find(s => s.notification_type === data.notification_type);
    
    if (existingSetting) {
      return await updateTenantNotificationSetting(existingSetting.settings_id, data);
    } else {
      return await createTenantNotificationSetting(data);
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update tenant notification settings');
  }
};

export const deleteTenantNotificationSetting = async (settingsId: string): Promise<void> => {
  try {
    await apiClient.delete(`${NOTIFICATION_BASE_URL}/tenant-settings/${settingsId}/`);
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete tenant notification setting');
  }
};

// Notification Logs API
export const fetchNotificationLogs = async (params?: {
  context?: string;
  tenant_id?: string;
  status?: string;
  channel_type?: string;
  notification_type?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}): Promise<NotificationLog[]> => {
  try {
    const response = await apiClient.get<ApiResponse<NotificationLog>>(`${NOTIFICATION_BASE_URL}/logs/`, {
      params
    });
    return response.data.results;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification logs');
  }
};

export const getNotificationLogStats = async (params?: {
  context?: string;
  tenant_id?: string;
  date_from?: string;
  date_to?: string;
}): Promise<{
  total_count: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  success_rate: number;
  channel_stats: Array<{ channel_type: string; count: number }>;
  type_stats: Array<{ notification_type: string; count: number }>;
  recent_activity: Array<{ created_at__date: string; count: number }>;
  total_cost: number;
}> => {
  try {
    const response = await apiClient.get(`${NOTIFICATION_BASE_URL}/logs/stats/`, {
      params
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notification stats');
  }
};

// Send Notifications API
export const sendNotification = async (data: SendNotificationRequest): Promise<{
  success: boolean;
  notification_id?: string;
  provider_message_id?: string;
  error?: string;
}> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/send/send_immediate/`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to send notification');
  }
};

export const sendNotificationAsync = async (data: SendNotificationRequest): Promise<{
  success: boolean;
  task_id?: string;
  message?: string;
}> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/send/send_async/`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to queue notification');
  }
};

export const sendBulkNotifications = async (notifications: SendNotificationRequest[]): Promise<{
  success: boolean;
  queued_count: number;
  error_count: number;
  task_ids: string[];
  errors: Array<{ notification: SendNotificationRequest; error: string }>;
}> => {
  try {
    const response = await apiClient.post(`${NOTIFICATION_BASE_URL}/send/send_bulk/`, {
      notifications
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to send bulk notifications');
  }
};

export const getNotificationTaskStatus = async (taskId: string): Promise<{
  task_id: string;
  status: string;
  result: any;
  traceback?: string;
}> => {
  try {
    const response = await apiClient.get(`${NOTIFICATION_BASE_URL}/send/task_status/`, {
      params: { task_id: taskId }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get task status');
  }
};

// Helper function to get current tenant ID
const getCurrentTenantId = (): string => {
  // This should be implemented based on your auth/tenant context
  // For now, return a placeholder
  return 'current_tenant';
};

// Convenience functions for tenant notifications
export const sendAppointmentReminder = async (
  patientId: string,
  patientEmail: string,
  templateVariables: Record<string, any>
): Promise<any> => {
  return sendNotificationAsync({
    notification_type: 'appointment_reminder',
    context: 'tenant',
    tenant_id: getCurrentTenantId(),
    recipient_type: 'patient',
    recipient_id: patientId,
    recipient_address: patientEmail,
    channel_type: 'email',
    template_variables: templateVariables
  });
};

export const sendTestResultsNotification = async (
  patientId: string,
  patientEmail: string,
  templateVariables: Record<string, any>
): Promise<any> => {
  return sendNotificationAsync({
    notification_type: 'test_results_available',
    context: 'tenant',
    tenant_id: getCurrentTenantId(),
    recipient_type: 'patient',
    recipient_id: patientId,
    recipient_address: patientEmail,
    channel_type: 'email',
    template_variables: templateVariables
  });
};

export const sendBillingStatement = async (
  patientId: string,
  patientEmail: string,
  templateVariables: Record<string, any>
): Promise<any> => {
  return sendNotificationAsync({
    notification_type: 'billing_statement',
    context: 'tenant',
    tenant_id: getCurrentTenantId(),
    recipient_type: 'patient',
    recipient_id: patientId,
    recipient_address: patientEmail,
    channel_type: 'email',
    template_variables: templateVariables
  });
};